'use client';

import { Button } from '@/components/button';
import { FormHelperText, FormLabel } from '@/components/field';
import { IconButton } from '@/components/iconButton';
import { truncateFileName } from '@/helper/text';
import DeleteIcon from '@/public/icons/loeschen.svg';
import { useFormikContext } from 'formik';
import Image from 'next/image';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import SubmitWrapper from '../helperTextWrapper/SubmitWrapper';
import InfoIconModal from '../infoIcon/InfoIconModal';
import classes from './UploadField.module.scss';
import { FILE_TYPE_MAP, convertToBytes } from './utils';

interface FileUploadProps {
  acceptedFileTypes?: string[];
  maxFileSize?: string; // in KB/MB
  maxFiles?: number;
  maxFilenameLength?: number;
  label?: string;
  infoText?: ReactNode;
  name: string;
  required: boolean;
  error?: boolean;
  externalErrorMessage?: string | false;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fileUploadText?: string;
}
const DEFAULT_MAX_FILE_SIZE = '5MB';
const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_FILE_NAME_LENGTH = 50; // Vgl. https://myswd.atlassian.net/browse/NEXT-1338

const UploadField: React.FC<FileUploadProps> = ({
  acceptedFileTypes = [],
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
  maxFilenameLength = DEFAULT_MAX_FILE_NAME_LENGTH,
  name,
  label,
  infoText,
  externalErrorMessage,
  error,
  required,
  onChange,
  fileUploadText = 'Foto oder Datei',
}: FileUploadProps) => {
  if (!acceptedFileTypes.length) {
    acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'application/pdf'];
  }
  const [fileEnter, setFileEnter] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadButtontouched, setUploadButtontouched] = useState(false);

  const { setFieldValue, values } = useFormikContext();

  const [files, setFiles] = useState<File[]>(() => {
    const initialFiles = ((values as any)[name] as File[]) || [];
    return Array.isArray(initialFiles) ? initialFiles : [];
  });

  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  // Build accept attribute list with common aliases to satisfy browser/file picker behavior
  const acceptList = React.useMemo(() => {
    const list = [...acceptedFileTypes];
    if (acceptedFileTypes?.includes('application/xml')) list.push('text/xml');
    if (acceptedFileTypes?.includes('text/xml')) list.push('application/xml');
    if (acceptedFileTypes?.includes('image/jpg')) list.push('image/jpeg');
    if (acceptedFileTypes?.includes('image/jpeg')) list.push('image/jpg');
    if (acceptedFileTypes?.includes('image/heic')) list.push('image/heif');
    if (acceptedFileTypes?.includes('image/heif')) list.push('image/heic');
    return Array.from(new Set(list));
  }, [acceptedFileTypes]);

  // check for allowed file type
  const isFileTypeAllowed = (type: string) => {
    if (!acceptedFileTypes) {
      return true;
    }

    // Normalize certain mime types that vary across browsers
    // - JPEG: some browsers report image/jpeg, and we accept image/jpg
    // - XML: browsers may report text/xml though we accept application/xml
    if (type === 'image/jpeg') type = 'image/jpg';
    if (type === 'text/xml') type = 'application/xml';

    return acceptedFileTypes?.some((acceptedType) => {
      return type === acceptedType;
    });
  };

  const isPreviewableImage = (file: File) => {
    const type = file.type || '';
    const name = file.name?.toLowerCase() || '';
    return type.startsWith('image/') || name.endsWith('.heic') || name.endsWith('.heif');
  };

  // accepted file types as string
  const formatAcceptedFileTypes = (): string => {
    if (acceptedFileTypes && acceptedFileTypes.length === 1 && acceptedFileTypes[0] === 'image/jpg') {
      acceptedFileTypes.push('image/jpeg');
    }
    return (
      acceptedFileTypes
        ?.map((type) => {
          // Find key in FILE_TYPE_MAP
          return Object.entries(FILE_TYPE_MAP).find(([_, val]) => val === type)?.[0];
        })
        .filter((key) => key !== undefined) // Filter undefined if present
        .join(', ') ?? ''
    );
  };

  /**
   * Handles the dropped and selected files
   */
  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;

    setErrorMessage('');
    setUploadButtontouched(false);

    // check max amount of files
    const totalFiles = files.length + newFiles.length;

    if (totalFiles > maxFiles) {
      setErrorMessage(t(`Es können maximal ${maxFiles} Dateien hochgeladen werden.`));
      setUploadButtontouched(true);
      return;
    }

    // check for allowed types and file size
    const newFileArray: File[] = Array.from(newFiles).filter((file) => {
      if (file.name.length > maxFilenameLength) {
        setErrorMessage(
          t(`Der Dateiname darf maximal ${maxFilenameLength} Zeichen lang sein. (Aktuell: ${file.name.length})`)
        );
        setUploadButtontouched(true);
        return false;
      }

      if (isFileTypeAllowed(file.type) && file.size <= convertToBytes(maxFileSize)) {
        return true;
      } else {
        setErrorMessage(
          t(
            `Datei ${truncateFileName(file.name)} hat keinen akzeptierten Dateityp oder überschreitet die zulässige Dateigröße von ${maxFileSize}.`
          )
        );
        setUploadButtontouched(true);
        return false;
      }
    });

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, ...newFileArray];
      return updatedFiles;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setFileEnter(false);

    handleFiles(e.dataTransfer.files);
  };

  // file remove
  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== index);
      return updatedFiles;
    });
  };

  /**
   * Simulates a file input change because the files are handled manually in the component
   */
  function simulateFileInputChange(files: File[]): React.ChangeEvent<HTMLInputElement> {
    // create file list for the event
    const fileList: FileList = {
      ...files,
      length: files.length,
      item(index: number): File | null {
        return files[index] || null;
      },
    };

    // create the file changed event
    const event: React.ChangeEvent<HTMLInputElement> = {
      target: { files: fileList } as any,
      currentTarget: { files: fileList } as any,
      bubbles: true,
      cancelable: true,
      defaultPrevented: false,
      isDefaultPrevented: () => false,
      eventPhase: 3,
      isTrusted: true,
      nativeEvent: new Event('change'),
      preventDefault: () => {},
      stopPropagation: () => {},
      isPropagationStopped: () => false,
      persist: () => {},
      timeStamp: Date.now(),
      type: 'change',
    };

    return event;
  }

  // Update Formik field value when files change
  useEffect(() => {
    onChange(simulateFileInputChange(files));
    setFieldValue(name, files);
  }, [files, name, setFieldValue]);

  // Convert HEIC files via server API and generate preview URLs; cleanup on unmount or when files change
  useEffect(() => {
    let revoked = false;
    let currentUrls: string[] = [];

    function isHeicFile(file: File): boolean {
      const lower = (file.name || '').toLowerCase();
      return (
        file.type === 'image/heic' || file.type === 'image/heif' || lower.endsWith('.heic') || lower.endsWith('.heif')
      );
    }

    async function convertHeicViaApi(file: File): Promise<File> {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/convert-heic', { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((err as { error?: string }).error ?? 'Conversion failed');
      }
      const blob = await res.blob();
      const baseName = (file.name || 'image').replace(/\.(heic|heif)$/i, '');
      return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
    }

    const run = async () => {
      previewUrls.forEach((url) => {
        if (url && typeof URL !== 'undefined' && typeof (URL as any).revokeObjectURL === 'function') {
          URL.revokeObjectURL(url);
        }
      });

      try {
        const hasHeic = files.some(isHeicFile);
        const resolvedFiles = hasHeic
          ? await Promise.all(files.map(async (file) => (isHeicFile(file) ? convertHeicViaApi(file) : file)))
          : files;

        if (hasHeic && !revoked) {
          setFiles(resolvedFiles);
        }

        const urls = resolvedFiles.map((file) => {
          try {
            return URL.createObjectURL(file);
          } catch {
            return '';
          }
        });

        if (!revoked) {
          currentUrls = urls;
          setPreviewUrls(urls);
        } else {
          urls.filter(Boolean).forEach((u) => {
            if (typeof URL !== 'undefined' && typeof (URL as any).revokeObjectURL === 'function') {
              URL.revokeObjectURL(u);
            }
          });
        }
      } catch (err) {
        console.error('Failed to generate preview for files', err);
        if (!revoked) setPreviewUrls(files.map(() => ''));
      }
    };

    run();

    return () => {
      revoked = true;
      currentUrls.forEach((url) => {
        if (url && typeof URL !== 'undefined' && typeof (URL as any).revokeObjectURL === 'function') {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [files]);

  return (
    <>
      <div className={classes.uploadFieldContainer}>
        <div className={classes.headline}>
          {(!!label || !required || infoText) && (
            <FormLabel className={classes.labelBox}>
              {!!label && `${label} `}
              {!required && <span className={classes.optional}>({t('Optional')})</span>}
              {!!infoText && (
                <>
                  <InfoIconModal trackingLabel={label ?? name}>{infoText}</InfoIconModal>{' '}
                </>
              )}
            </FormLabel>
          )}
        </div>

        <div
          className={`${classes.dropzone} ${fileEnter ? classes['border-drag-over'] : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setFileEnter(true);
          }}
          onDragLeave={() => {
            setFileEnter(false);
          }}
          onDragEnd={(e) => {
            e.preventDefault();
            setFileEnter(false);
          }}
          onDrop={handleDrop}
        >
          {files.length < maxFiles && (
            <div className={classes.controls}>
              <label htmlFor="file" className={classes.infoLabel}>
                {t(`${fileUploadText} mit Drag & Drop hier anhängen oder`)}
              </label>

              <SubmitWrapper
                formikErrors={{ errorMessage, externalErrorMessage }}
                formikTouched={{ errorMessage: uploadButtontouched, externalErrorMessage: uploadButtontouched }}
              >
                <Button
                  data-testid="upload-file-button"
                  variant="ctaWhite"
                  className={classes.button}
                  onClick={() => inputRef.current?.click()}
                >
                  {t('Datei auswählen')}
                </Button>
              </SubmitWrapper>
              <span className={classes.infoFiletypes}>
                {t('Erlaubte Dateien:') + ' ' + formatAcceptedFileTypes() + `. Maximale Dateigröße: ${maxFileSize}`}
              </span>
            </div>
          )}
          <input
            id={name}
            name={name}
            type="file"
            ref={inputRef}
            required={required}
            className={classes.input}
            multiple={maxFiles > 1}
            accept={acceptList.join(',')}
            onChange={(e) => {
              handleFiles(e.target.files);
            }}
            data-testid="upload-file-input"
          />

          {files.length > 0 && (
            <div className={classes.previewContainer}>
              {files.map((file, index) => (
                <div key={index} className={classes.previewItem}>
                  {!isPreviewableImage(file) || !previewUrls[index] ? (
                    <div className={classes.previewFile}>{truncateFileName(file.name)}</div>
                  ) : (
                    <Image
                      className={classes.previewImage}
                      width={file.size}
                      height={file.size}
                      alt={file.name}
                      src={previewUrls[index]!}
                    />
                  )}

                  <IconButton
                    size="small"
                    aria-label="Datei löschen"
                    onClick={() => handleRemoveFile(index)}
                    data-testid="file-delete-button"
                    className={classes.removeButton}
                  >
                    <DeleteIcon aria-hidden="true" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={classes.error}>
          {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          {error && externalErrorMessage && <FormHelperText>{externalErrorMessage}</FormHelperText>}
        </div>
      </div>
    </>
  );
};

export default UploadField;
